import { SettingsPermissions } from "@redotech/account-common/permissions";
import { Json } from "@redotech/json/json";
import { OutletFade } from "@redotech/react-animation/outlet-fade";
import {
  OptionalStringParam,
  useParam,
} from "@redotech/react-router-util/param";
import { useHandler } from "@redotech/react-util/hook";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import {
  ReturnFlow as ModelReturnFlow,
  returnFlowJsonFormat,
} from "@redotech/redo-return-core-common/return-flow";
import { FlowType } from "@redotech/redo-return-core-common/return-flow-enums";
import {
  DetailsComponentsForSteps,
  modelToSteps,
  narrowToReturnFlow,
  NullDetails,
  stepsToModel,
} from "@redotech/redo-return-ui/flows/model-to-steps";
import { remintAllStateUuids } from "@redotech/redo-return-ui/flows/remint-state-uuids";
import {
  Step,
  StepId,
  StepSelectFn,
  StepType,
  TypedStep,
} from "@redotech/redo-return-ui/flows/step";
// import { Message } from "@redotech/redo-web/ai/chat/components/chat-with-thread/types";
import { useTranslation } from "@redotech/redo-i18n";
import TrashIcon from "@redotech/redo-icons/trash.svg";
import { AddConversationTagDetails } from "@redotech/redo-merchant-support-app/setting/chat-flow/add-conversation-tag";
import { AssignToAgentDetails } from "@redotech/redo-merchant-support-app/setting/chat-flow/assign-to-agent";
import { ChatMultipleChoiceDetails } from "@redotech/redo-merchant-support-app/setting/chat-flow/chat-multiple-choice";
import { CloseChatDetails } from "@redotech/redo-merchant-support-app/setting/chat-flow/close-chat";
import { CustomerResponseDetails } from "@redotech/redo-merchant-support-app/setting/chat-flow/customer-response";
import { ReentryDetails } from "@redotech/redo-merchant-support-app/setting/chat-flow/reentry";
import { SeeOrdersDetails } from "@redotech/redo-merchant-support-app/setting/chat-flow/see-orders";
import { SendMessageDetails } from "@redotech/redo-merchant-support-app/setting/chat-flow/send-message";
import {
  DetailsForActions,
  MultipleActionsDetails,
} from "@redotech/redo-merchant-support-app/setting/rules/multiple-actions";
import {
  DetailsForRuleConditions,
  MultipleConditionsDetails,
} from "@redotech/redo-merchant-support-app/setting/rules/multiple-conditions";
import { RuleDetailsDrawer } from "@redotech/redo-merchant-support-app/setting/rules/rule-details-drawer";
import * as selectCss from "@redotech/redo-return-ui/flows/return-flow/select.module.css";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { Card, CardTheme } from "@redotech/redo-web/card";
import { Flex } from "@redotech/redo-web/flex";
import {
  BlockConfig,
  EdgeConfig,
  Flowchart,
  SelectionMode,
  SelectionTheme,
} from "@redotech/redo-web/flowchart";
import * as gridCss from "@redotech/redo-web/grid.module.css";
import CheckIcon from "@redotech/redo-web/icon-old/check-thin.svg";
import EditPencilIcon from "@redotech/redo-web/icon-old/edit-pencil.svg";
import InfoIcon from "@redotech/redo-web/icon-old/info.svg";
import CloseIcon from "@redotech/redo-web/icon-old/modal-close-button.svg";
import XIcon from "@redotech/redo-web/icon-old/x.svg";
import { LabeledInput } from "@redotech/redo-web/labeled-input";
import {
  ActionPortalContext,
  useScrollableHeight,
  useScrollableWidth,
} from "@redotech/redo-web/page";
import { SelectDropdown } from "@redotech/redo-web/select-dropdown";
import { TextInput } from "@redotech/redo-web/text-input";
import { sinkPromise } from "@redotech/util/promise";
import classnames from "classnames";
import cloneDeep from "lodash/cloneDeep";
import {
  Dispatch,
  memo,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { CombineLabelDetails } from "../label-flow/combine-label";
import { SeparateLabelsDetails } from "../label-flow/separate-labels";
import { DonateDetails } from "../location-flow/donate-details";
import { RedoManagedLocationDetails } from "../location-flow/redo-managed-location";
import { SetReturnLocationsDetails } from "../location-flow/set-return-locations";
import { AbTestDetails } from "../return-flow/ab-test";
import { AddExchangeOrderTagsDetails } from "../return-flow/add-exchange-order-tags";
import { AddReturnTagDetails } from "../return-flow/add-return-tag";
import { AddUndeclaredItemDetails } from "../return-flow/add-undeclared-item";
import { AiReturnReasonsDetails } from "../return-flow/ai-return-reasons";
import { BlockDetails } from "../return-flow/block";
import {
  ConditionDetails,
  getDetailsForCondition,
} from "../return-flow/condition";
import { DiscountCodeNameDetails } from "../return-flow/discount-code-name";
import { DoNothingDetails } from "../return-flow/do-nothing";
import { DynamicBundleAdjustmentDetails } from "../return-flow/dynamic-bundle-adjustment";
import { EMPTY } from "../return-flow/empty";
import { EvidenceDetails } from "../return-flow/evidence";
import { ExchangeShippingNameDetails } from "../return-flow/exchange-shipping-name";
import { ExchangeShippingRatesDetails } from "../return-flow/exchange-shipping-rates";
import { FinalSaleReturnDetails } from "../return-flow/final-sale";
import { FlagDetails } from "../return-flow/flag";
import { InformationDetails } from "../return-flow/information";
import { InputDetails } from "../return-flow/input";
import { MakeServiceRequestDetails } from "../return-flow/make-service-request";
import { ManualReviewDetails } from "../return-flow/manual-review";
import { MerchantReviewBeforeFilingDetails } from "../return-flow/merchant-review-before-filing";
import { MultipleChoiceDetails } from "../return-flow/multiple-choice";
import { OverrideAdjustmentDetails } from "../return-flow/override-adjustment";
import { OverrideGreenReturnDetails } from "../return-flow/override-green-return";
import { ProcessingActionDetails } from "../return-flow/processing-action";
import { RejectDetails } from "../return-flow/reject";
import { ReturnDetails } from "../return-flow/return";
import * as returnFlowCss from "../return-flow/return-flow.module.css";
import { ReturnProcessingDecisionDetails } from "../return-flow/return-processing-decision";
import { ReturnProcessingInformationDetails } from "../return-flow/return-processing-information";
import { SelectStep, StepSelect } from "../return-flow/select";
import { SendEmailDetails } from "../return-flow/send-email";
import { SendSmsDetails } from "../return-flow/send-sms";
import { SortDetails } from "../return-flow/sort";
import { StartDetails } from "../return-flow/start";
import { SubmitClaimDetails } from "../return-flow/submit-claim";
import { SubmitRepairDetails } from "../return-flow/submit-repair";
import { SubmitWarrantyDetails } from "../return-flow/submit-warranty";
import { SubmitWarrantyRegistrationDetails } from "../return-flow/submit-warranty-registration";
import { TransformProductDetails } from "../return-flow/transform-product";
import { TriggerDetails } from "../return-flow/trigger";
import { VerificationDetails } from "../return-flow/verification";
import { VerifyItemVariantDetails } from "../return-flow/verify-item-variant";
import { WaitDetails } from "../return-flow/wait";
import { FlowEditorHeaderButtons } from "./flow-editor-header-buttons";
import { InspectorErrorBoundary } from "./inspector-error-boundary";
import {
  localizeFlowSidebarTitle,
  localizeFlowStepTitle,
} from "./utils/localized-step-title";

export const FLOW_TYPES = {
  RETURN: "return",
  FINALIZE: "finalize",
  CLAIM: "claim",
  CHAT: "chat",
  WARRANTY: "warranty",
};

let stepCounter = 0;

export const DetailsForSteps: DetailsComponentsForSteps = {
  Start: StartDetails,
  MultipleChoice: MultipleChoiceDetails,
  Flag: FlagDetails,
  ManualReview: ManualReviewDetails,
  MerchantReviewBeforeFiling: MerchantReviewBeforeFilingDetails,
  DoNothing: DoNothingDetails,
  SubmitWarrantyRegistration: SubmitWarrantyRegistrationDetails,
  Input: InputDetails,
  Information: InformationDetails,
  ExchangeShippingName: ExchangeShippingNameDetails,
  ExchangeShippingRates: ExchangeShippingRatesDetails,
  DiscountCodeName: DiscountCodeNameDetails,
  Reject: RejectDetails,
  OverrideAdjustment: OverrideAdjustmentDetails,
  OverrideGreenReturn: OverrideGreenReturnDetails,
  Return: ReturnDetails,
  FinalSaleReturn: FinalSaleReturnDetails,
  SubmitClaim: SubmitClaimDetails,
  SubmitWarranty: SubmitWarrantyDetails,
  SubmitRepair: SubmitRepairDetails,
  Block: BlockDetails,
  ChatMultipleChoice: ChatMultipleChoiceDetails,
  CloseChat: CloseChatDetails,
  CustomerResponse: CustomerResponseDetails,
  AssignToAgent: AssignToAgentDetails,
  SendMessage: SendMessageDetails,
  Trigger: TriggerDetails,
  Wait: WaitDetails,
  SendEmail: SendEmailDetails,
  SendSms: SendSmsDetails,
  SeeOrders: SeeOrdersDetails,
  AddConversationTag: AddConversationTagDetails,
  Reentry: ReentryDetails,
  AbTest: AbTestDetails,
  AddExchangeOrderTags: AddExchangeOrderTagsDetails,
  Condition: ConditionDetails,
  MultipleConditions: MultipleConditionsDetails,
  MultipleActions: MultipleActionsDetails,
  AiReturnReasons: AiReturnReasonsDetails,
  AddReturnTag: AddReturnTagDetails,
  DynamicBundleAdjustment: DynamicBundleAdjustmentDetails,
  SetReturnLocations: SetReturnLocationsDetails,
  Donate: DonateDetails,
  CombineLabel: CombineLabelDetails,
  SeparateLabels: SeparateLabelsDetails,
  ReturnProcessingDecision: ReturnProcessingDecisionDetails,
  ReturnProcessingInformation: ReturnProcessingInformationDetails,
  Sort: SortDetails,
  Evidence: EvidenceDetails,
  AddUndeclaredItem: AddUndeclaredItemDetails,
  Verification: VerificationDetails,
  ProcessingAction: ProcessingActionDetails,
  VerifyItemVariant: VerifyItemVariantDetails,
  MakeServiceRequest: MakeServiceRequestDetails,
  TransformProduct: TransformProductDetails,
  RedoManagedLocation: RedoManagedLocationDetails,
  Compensation: NullDetails,
  Restocking: NullDetails,
  PhaseAnchor: NullDetails,
  EndNode: NullDetails,
};

export const modelToStepsDetailsProps = {
  getDetailsForCondition,
  DetailsForSteps,
  DetailsForActions,
  DetailsForRuleConditions,
};

export const FlowEditor = memo(function FlowEditor({
  questionFlow,
  handleSave,
  saveLoad,
  publishLoad,
  defaultQuestionFlow,
  firstStepTitle,
  stepOptions,
  returnReasonTitle,
  flowType,
  handlePublish,
  handleUnpublish,
  ruleDetails,
  defaultButtonClicked,
  defaultButtonPending,
  defaultButtonText,
  onFlowStepChange,
  isNewEditor,
  toggleEditorMode,
}: {
  // Comes from data stored in team.settings.{flowtype}
  questionFlow: Json;
  handleSave: (
    newFlow: Json,
    signal?: AbortSignal,
    ruleName?: string,
    ruleDescription?: string,
  ) => Promise<void>;
  saveLoad: { pending: boolean };
  publishLoad?: { pending: boolean };
  handlePublish?: () => Promise<void>;
  handleUnpublish?: () => Promise<void>;
  defaultQuestionFlow: ModelReturnFlow;
  firstStepTitle?: string;
  stepOptions: StepType<any, any>[];
  returnReasonTitle?: string;
  flowType: FlowType;
  ruleDetails?: { name: string | undefined; description: string | undefined };
  defaultButtonClicked?: () => void;
  defaultButtonPending?: boolean;
  defaultButtonText?: string;
  onFlowStepChange?: (
    changedStep: { id: StepId; step: Readonly<Step> },
    existingSteps: ReadonlyMap<StepId, Readonly<Step>>,
    setStep: (id: StepId, step: SetStateAction<Step | undefined>) => void,
  ) => void;
  isNewEditor?: boolean;
  toggleEditorMode?: () => void;
}) {
  const { t } = useTranslation();
  useScrollableWidth();
  useScrollableHeight();

  const team = useContext(TeamContext);
  const actionsElement = useContext(ActionPortalContext);

  const [selectedParam] = useParam(new OptionalStringParam("selected"));

  const getStoredSelection = () => {
    return (
      sessionStorage.getItem(`flow-editor-selected-${flowType}`) || undefined
    );
  };

  const storedSelection = getStoredSelection();
  const hasStoredSelection = storedSelection !== undefined;

  const [selected, setSelected] = useState<string | undefined>(
    selectedParam ??
      storedSelection ??
      (!hasStoredSelection ? "start" : undefined),
  );

  const handleSelectedChange = useCallback(
    (value: string | undefined) => {
      if (value === undefined) {
        sessionStorage.removeItem(`flow-editor-selected-${flowType}`);
      } else {
        sessionStorage.setItem(`flow-editor-selected-${flowType}`, value);
      }
      setSelected(value);
    },
    // FIXME

    [flowType],
  );

  useEffect(() => {
    const storedSelection = getStoredSelection();
    if (selectedParam !== storedSelection && storedSelection !== undefined) {
      handleSelectedChange(storedSelection);
    }
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flowType, selectedParam]);

  const [showDetailsDrawer, setShowDetailsDrawer] = useState(false);

  const [modified, setModified] = useState(false);
  const [published, setPublished] = useState<boolean | undefined>(undefined);

  const initialRuleName = ruleDetails?.name || "";
  const initialRuleDescription = ruleDetails?.description || "";

  const [ruleName, setRuleName] = useState<string>(initialRuleName);
  const [ruleDescription, setRuleDescription] = useState<string>(
    initialRuleDescription,
  );

  const [steps, setSteps] = useState(new Map<StepId, Step>());

  const reset = () => {
    if (!team || !questionFlow || !defaultQuestionFlow) {
      return;
    }
    let questionFlowReset = returnFlowJsonFormat.read(questionFlow);
    const hasExistingFlow = questionFlowReset.steps.length > 0;
    // There isn't a flow saved. Loading the default flow.
    if (!hasExistingFlow) {
      questionFlowReset = defaultQuestionFlow;
      setModified(true);
    }
    setSteps(
      modelToSteps({
        model: questionFlowReset,
        firstStepTitle,
        returnReasonTitle,
        flowType,
        ...modelToStepsDetailsProps,
      }),
    );
    if (hasExistingFlow) {
      setPublished(!!questionFlowReset.publishedAt);
      setModified(false);
    }
  };

  // FIXME
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(reset, [team, questionFlow, defaultQuestionFlow]);

  const [selectNode, setSelectNode] = useState<SelectStep | undefined>();

  const [hover, setHover] = useState<string[]>([]);

  useEffect(() => {
    if (
      initialRuleDescription !== ruleDescription ||
      initialRuleName !== ruleName
    ) {
      setModified(true);
    }
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ruleName, ruleDescription]);

  const create = useHandler((existingStepId?: StepId) => {
    const id = `new.${stepCounter++}`;
    let step;
    if (existingStepId) {
      const existingStep = steps.get(existingStepId);
      if (existingStep && !existingStep.start) {
        step = remintAllStateUuids(
          new TypedStep(
            existingStep.type,
            existingStep.type.fromModel(
              existingStep.type.toModel(
                cloneDeep(existingStep.state),
                // The model conversion is immediately reversed below; this
                // callback only supplies the unused next-step ID.
                // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
                (_id) => undefined as unknown as number,
              ),
              String,
            ),
          ),
        );
      }
    }
    if (!step) {
      step = new TypedStep(EMPTY, undefined);
    }
    setSteps((steps) => {
      steps = new Map(steps);
      steps.set(id, step);
      return steps;
    });
    handleSelectedChange(id);
    setModified(true);
    return id;
  });

  const setStepWithId = useHandler(
    (stepId: StepId, step: SetStateAction<Step | undefined>) => {
      setSteps((steps) => {
        if (step instanceof Function) {
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          step = step(steps.get(stepId)!);
        }
        if (step) {
          steps = new Map(steps);
          steps.set(stepId, step);
        } else {
          steps = new Map(
            [...steps.entries()]
              .filter(([id]) => id !== stepId)
              .map(([id, step]) => [id, step.stepDeleted(stepId)]),
          );
        }
        return steps;
      });
      setModified(true);
    },
  );

  const setStep = useHandler<Dispatch<SetStateAction<Step | undefined>>>(
    (step) => {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      setStepWithId(selected!, step);
    },
  );

  const onStepChange = useHandler((step: Step) => {
    if (selected && onFlowStepChange) {
      onFlowStepChange({ id: selected, step }, steps, setStepWithId);
    }
  });

  const onSelectionChange = useHandler((selected: StepId | undefined) => {
    if (selected) {
      const step = steps.get(selected);
      if (step) {
        onStepChange?.(step);
      }
    }
  });

  const onSelect = useHandler((value: StepId | undefined) => {
    onSelectionChange(selected);

    if (selectNode) {
      selectNode(value);
    } else {
      setShowDetailsDrawer(false);
      handleSelectedChange(value);
    }
  });

  const selectNodeHandler = useHandler((value: SelectStep) =>
    setSelectNode(() => value),
  );

  function getSaveDisabledMessage(): string | undefined {
    if (saveLoad.pending) {
      return t("Saving your changes...");
    }
    if (flowType === FlowType.RULE && !ruleName) {
      return t("You must name the rule before saving");
    }
    const hasInvalidStep = [...steps.values()].some(
      (step) => !step.valid(flowType),
    );
    if (hasInvalidStep) {
      return t(
        "One or more of your steps are invalid. Update the steps then try again.",
      );
    }
    return undefined;
  }

  const detailsClicked = () => {
    onSelectionChange(selected);
    handleSelectedChange(undefined);
    setShowDetailsDrawer((shouldShow) => !shouldShow);
  };

  const cancelClicked = () => {
    reset();
    setSelectNode(undefined);
  };

  const saveClicked = async () => {
    if (getSaveDisabledMessage() !== undefined) {
      // Keyboard shortcuts can bypass the disabled Save button.
      return;
    }
    onSelectionChange(selected);
    try {
      await handleSave(
        returnFlowJsonFormat.write(narrowToReturnFlow(stepsToModel(steps))),
        undefined,
        ruleName,
        ruleDescription,
      );
    } catch {
      // The caller surfaces the failure. Stay modified so Save remains visible.
      return;
    }
    setModified(false);
  };

  const publishClicked = () => {
    if (handlePublish) {
      sinkPromise(handlePublish());
    }
  };

  const unpublishClicked = () => {
    if (handleUnpublish) {
      sinkPromise(handleUnpublish());
    }
  };

  const flowEditorHeaderButtons = (
    <FlowEditorHeaderButtons
      cancelClicked={cancelClicked}
      cancelDisabled={saveLoad.pending}
      defaultButtonClicked={defaultButtonClicked}
      defaultButtonPending={defaultButtonPending}
      defaultButtonText={defaultButtonText}
      detailsClicked={detailsClicked}
      flowType={flowType}
      isNewEditor={isNewEditor}
      publishClicked={publishClicked}
      publishPending={publishLoad?.pending ?? false}
      rightPanelOpen={!!selected || showDetailsDrawer}
      ruleName={ruleName}
      saveClicked={saveClicked}
      saveDisabled={getSaveDisabledMessage()}
      savePending={saveLoad.pending}
      setRuleName={setRuleName}
      showPublishButton={!modified && !!handlePublish && published === false}
      showSaveAndCancel={modified}
      showUnpublishButton={!modified && !!handleUnpublish && published === true}
      toggleEditorMode={toggleEditorMode}
      unpublishClicked={unpublishClicked}
    />
  );

  return (
    <>
      {actionsElement
        ? createPortal(flowEditorHeaderButtons, actionsElement)
        : null}

      {/* design-allow: legacy CSS grid wrapper controls canvas layout. */}
      <div
        className={classnames(
          returnFlowCss.container,
          gridCss.grid,
          gridCss.stretch,
        )}
      >
        <Flex
          className={classnames(returnFlowCss.column, gridCss.span12)}
          dir="column"
          gap="none"
          position="relative"
        >
          <Diagram
            flowType={flowType}
            highlight={!!selectNode}
            hover={hover}
            onSelect={onSelect}
            recenterOnStepChange={false}
            selected={selected}
            steps={steps}
          />
        </Flex>
        <div
          className={classnames(
            returnFlowCss.sideDrawer,
            selected !== undefined && returnFlowCss.sideDrawerOpen,
          )}
        >
          {selected !== undefined && (
            <InspectorErrorBoundary key={selected}>
              <Detail
                canClose={!selectNode}
                create={create}
                flowType={flowType}
                handleClose={() => handleSelectedChange(undefined)}
                id={selected}
                setHover={setHover}
                setSelectNodes={selectNodeHandler}
                setStep={setStep}
                setStepWithId={setStepWithId}
                step={steps.get(selected!)} // eslint-disable-line @typescript-eslint/no-non-null-assertion
                stepOptions={stepOptions}
                steps={steps}
              />
            </InspectorErrorBoundary>
          )}
        </div>
        <div
          className={classnames(
            returnFlowCss.sideDrawer,
            showDetailsDrawer && returnFlowCss.sideDrawerOpen,
          )}
        >
          <OutletFade
            childClassName={returnFlowCss.detailsContent}
            key_="FlowDetail"
          >
            <header className={returnFlowCss.headerWithClose}>
              <div className={returnFlowCss.closeButton}>
                <RedoButton
                  aria-label={t("Close")}
                  hierarchy="tertiary"
                  IconLeading={CloseIcon}
                  onClick={(event) => {
                    setShowDetailsDrawer(false);
                    event.stopPropagation();
                  }}
                  size="md"
                  theme="normal"
                />
              </div>
            </header>
            <RuleDetailsDrawer
              description={ruleDescription}
              name={ruleName}
              setDescription={(val) => {
                setRuleDescription(val);
                setModified(true);
              }}
              setName={(val) => {
                setRuleName(val);
                setModified(true);
              }}
            />
          </OutletFade>
        </div>
      </div>
    </>
  );
});

const Diagram = memo(function Diagram({
  highlight,
  hover,
  selected,
  steps,
  onSelect,
  flowType,
  recenterOnStepChange,
}: {
  highlight: boolean;
  hover: string[];
  steps: Map<string, Step>;
  selected: string | undefined;
  onSelect(id: string): void;
  flowType: FlowType;
  recenterOnStepChange: boolean;
}) {
  const team = useContext(TeamContext);
  const selectionMode: SelectionMode = useMemo(
    () => ({
      enabled: (id) =>
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        !highlight || (steps.get(id)!.start === undefined && selected !== id),
      theme: highlight ? SelectionTheme.HIGHLIGHT : SelectionTheme.NORMAL,
    }),
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [highlight, steps],
  );

  const blocks: BlockConfig[] = [...steps.entries()].map(([id, step]) => ({
    error: !step.valid(flowType),
    layout: step.layout(),
    title: step.title(),
    description: step.description(team, flowType),
    icon: <step.Icon className={returnFlowCss.icon} />,
    additionalIcon: step.additionalIcon(team, flowType),
    id,
  }));

  const edges: EdgeConfig[] = [...steps.entries()].flatMap(([id, step]) =>
    step
      .downstream()
      .filter((downstream) => downstream.id !== undefined)
      .map((downstream) => ({
        id: `${id}.${downstream.id}`,
        from: id,
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        to: downstream.id!,
        label: downstream.label,
      })),
  );

  return (
    <Card
      className={returnFlowCss.center}
      noPadding
      scrollable
      theme={CardTheme.DARK}
    >
      <Flowchart
        blocks={blocks}
        edges={edges}
        hover={hover}
        onSelect={onSelect}
        recenterOnStepChange={recenterOnStepChange}
        selected={selected}
        selectionMode={selectionMode}
      />
    </Card>
  );
});

const Detail = memo(function Detail({
  create,
  id,
  setHover,
  setSelectNodes,
  setStep,
  setStepWithId,
  canClose,
  handleClose,
  step,
  steps,
  stepOptions,
  flowType,
}: {
  create(existingStepId?: StepId): StepId;
  id: string | undefined;
  setHover(id: StepId[]): void;
  setSelectNodes(fn: SelectStep | undefined): void;
  setStep: Dispatch<SetStateAction<Step | undefined>>;
  setStepWithId: (id: StepId, step: SetStateAction<Step | undefined>) => void;
  canClose: boolean;
  handleClose(): void;
  step: Step | undefined;
  steps: Map<string, Step>;
  stepOptions: StepType<any, any>[];
  flowType: FlowType;
}) {
  const { t } = useTranslation();
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [title, setTitle] = useState<string>();
  // TODO: Not sure what the right permission mix is
  const canEditSupportSettings = useHasPermission(
    SettingsPermissions.SET_SUPPORT_SETTINGS,
  );
  const canEditReturnsSettings = useHasPermission(
    SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
  );
  const canEditSettings = canEditSupportSettings || canEditReturnsSettings;
  const typeChanged = useHandler((type: StepType<any, any> | undefined) => {
    type = type || EMPTY;
    setStep(
      (step) =>
        step &&
        new TypedStep(
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          type!,
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          type!.defaultInitialStateFn?.(steps) ?? type!.empty,
        ),
    );
  });

  const handleTitleChange = useHandler((newTitle: string) => {
    step?.setCustomTitle(newTitle, setStep);
  });

  const stepSelect: StepSelectFn = ({ value, valueChange, type }) => {
    return (
      <StepSelect
        create={create}
        disabled={!canEditSettings}
        onHover={(hover) =>
          setHover(hover && value !== undefined ? [value] : [])
        }
        setSelectStep={setSelectNodes}
        step={steps.get(value!)} // eslint-disable-line @typescript-eslint/no-non-null-assertion
        type={type}
        value={value}
        valueChange={valueChange}
      />
    );
  };

  useEffect(() => {
    setTitle(undefined);
    setIsEditingTitle(false);
  }, [step]);

  return (
    <div>
      <OutletFade
        childClassName={returnFlowCss.detailsContent}
        key_={`${id || ""}.${step ? step.title() : ""}`}
      >
        {step && (
          <>
            <header className={returnFlowCss.headerWithClose}>
              {canClose && (
                <div className={returnFlowCss.closeButton}>
                  <RedoButton
                    aria-label={t("Close")}
                    hierarchy="tertiary"
                    IconLeading={CloseIcon}
                    onClick={(event) => {
                      handleClose();
                      event.stopPropagation();
                    }}
                    size="md"
                    theme="normal"
                  />
                </div>
              )}
              <div className={returnFlowCss.detailHeader}>
                <div className={returnFlowCss.detailTitle}>
                  {isEditingTitle ? (
                    <TextInput
                      button={
                        <div className={returnFlowCss.cancelConfirm}>
                          <RedoButton
                            aria-label={t("Cancel")}
                            hierarchy="tertiary"
                            IconLeading={XIcon}
                            onClick={(event) => {
                              setIsEditingTitle(false);
                              event.stopPropagation();
                            }}
                            size="md"
                            theme="normal"
                          />
                          <RedoButton
                            aria-label={t("Confirm")}
                            disabled={!title}
                            hierarchy="tertiary"
                            IconLeading={CheckIcon}
                            onClick={(event) => {
                              handleTitleChange(title || step.sidebarTitle());
                              setIsEditingTitle(false);
                              event.stopPropagation();
                            }}
                            size="md"
                            theme="normal"
                          />
                        </div>
                      }
                      onChange={(newTitle) => setTitle(newTitle)}
                      value={title ?? step.sidebarTitle()}
                    />
                  ) : (
                    <>
                      <h2>
                        {localizeFlowSidebarTitle(
                          step.sidebarTitle(),
                          step.type.title,
                        )}
                      </h2>
                      {step.start === undefined &&
                        step.sidebarTitle() !== "New step" && (
                          <RedoButton
                            aria-label={t("Edit")}
                            hierarchy="tertiary"
                            IconLeading={EditPencilIcon}
                            onClick={(event) => {
                              setTitle(step.sidebarTitle());
                              setIsEditingTitle(true);
                              event.stopPropagation();
                            }}
                            size="md"
                            theme="normal"
                          />
                        )}
                    </>
                  )}
                </div>
                {step.start === undefined &&
                  canEditSettings &&
                  !isEditingTitle && (
                    <RedoButton
                      aria-label={t("Delete")}
                      hierarchy="tertiary"
                      IconLeading={TrashIcon}
                      onClick={(event) => {
                        setStep(undefined);
                        event.stopPropagation();
                      }}
                      size="md"
                      theme="normal"
                    />
                  )}
              </div>
            </header>
            {step.information() && (
              <div className={returnFlowCss.informationContainer}>
                <InfoIcon className={returnFlowCss.infoIcon} />
                <div>{step.information()}</div>
              </div>
            )}
            {step.start === undefined && (
              <LabeledInput label={t("Step type")}>
                <SelectDropdown
                  disabled={!canEditSettings}
                  keyFn={(type) => type.title}
                  options={stepOptions}
                  value={step.type}
                  valueChange={typeChanged}
                >
                  {(type) => (
                    <div className={selectCss.value}>
                      <type.Icon className={selectCss.icon} />
                      <div className={selectCss.title}>
                        {localizeFlowStepTitle(type.title)}
                      </div>
                    </div>
                  )}
                </SelectDropdown>
              </LabeledInput>
            )}
            {step.details({
              stepSelect,
              setStep,
              setStepWithId,
              flowType,
              steps,
            })}
          </>
        )}
      </OutletFade>
    </div>
  );
});
